/**
 * Menu Builder Service
 * 
 * Service layer for menu builder operations.
 * Handles database interactions for menu layouts.
 */

import { supabase } from '@/integrations/supabase/client';
import { 
  MenuBuilderLayout, 
  MenuBuilderLayoutRecord,
  createEmptyLayout 
} from '@/types/menuBuilder';

/**
 * Custom error class for service errors
 */
export class MenuBuilderServiceError extends Error {
  code: string;
  details?: unknown;

  constructor(message: string, code: string = 'UNKNOWN', details?: unknown) {
    super(message);
    this.name = 'MenuBuilderServiceError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Fetch menu builder layout for a branch
 */
export async function fetchMenuBuilderLayout(
  branchId: string
): Promise<MenuBuilderLayoutRecord | null> {
  if (!branchId) {
    throw new MenuBuilderServiceError('Branch ID is required', 'INVALID_INPUT');
  }

  const { data, error } = await supabase
    .from('menu_builder_layouts')
    .select('*')
    .eq('branch_id', branchId)
    .maybeSingle();

  if (error) {
    throw new MenuBuilderServiceError(
      error.message, 
      error.code, 
      error
    );
  }

  // Type cast the layout_data from Json to MenuBuilderLayout
  if (data) {
    return {
      ...data,
      layout_data: data.layout_data as unknown as MenuBuilderLayout,
    };
  }

  return null;
}

/**
 * Fetch published menu layout for public menu (no auth required)
 */
export async function fetchPublishedMenuLayout(
  branchId: string
): Promise<MenuBuilderLayout | null> {
  if (!branchId) {
    return null;
  }

  const { data, error } = await supabase
    .rpc('get_published_menu_layout', {
      p_branch_id: branchId,
    });

  if (error) {
    console.error('Error fetching published layout:', error);
    return null;
  }

  if (!data) {
    return null;
  }

  return data as unknown as MenuBuilderLayout;
}

/**
 * Save menu builder layout
 * Supports optimistic locking via expectedVersion
 */
export async function saveMenuBuilderLayout(
  branchId: string,
  layoutData: MenuBuilderLayout,
  expectedVersion?: number
): Promise<MenuBuilderLayoutRecord> {
  if (!branchId) {
    throw new MenuBuilderServiceError('Branch ID is required', 'INVALID_INPUT');
  }

  if (!layoutData || !Array.isArray(layoutData.blocks)) {
    throw new MenuBuilderServiceError(
      'Invalid layout data: blocks array is required', 
      'INVALID_INPUT'
    );
  }

  const { data, error } = await supabase.rpc('save_menu_builder_layout', {
    p_branch_id: branchId,
    p_layout_data: layoutData as unknown as Record<string, unknown>,
    p_expected_version: expectedVersion ?? null,
  });

  if (error) {
    // Handle version conflict specifically
    if (error.message.includes('Version conflict')) {
      throw new MenuBuilderServiceError(
        'Someone else has modified this layout. Please refresh and try again.',
        'VERSION_CONFLICT',
        error
      );
    }
    
    throw new MenuBuilderServiceError(
      error.message, 
      error.code, 
      error
    );
  }

  return {
    ...data,
    layout_data: data.layout_data as unknown as MenuBuilderLayout,
  };
}

/**
 * Publish menu builder layout (makes it live for customers)
 */
export async function publishMenuBuilderLayout(
  branchId: string
): Promise<void> {
  if (!branchId) {
    throw new MenuBuilderServiceError('Branch ID is required', 'INVALID_INPUT');
  }

  const { error } = await supabase.rpc('publish_menu_builder_layout', {
    p_branch_id: branchId,
  });

  if (error) {
    throw new MenuBuilderServiceError(
      error.message, 
      error.code, 
      error
    );
  }
}

/**
 * Unpublish menu builder layout (revert to default menu)
 */
export async function unpublishMenuBuilderLayout(
  branchId: string
): Promise<void> {
  if (!branchId) {
    throw new MenuBuilderServiceError('Branch ID is required', 'INVALID_INPUT');
  }

  const { error } = await supabase.rpc('unpublish_menu_builder_layout', {
    p_branch_id: branchId,
  });

  if (error) {
    throw new MenuBuilderServiceError(
      error.message, 
      error.code, 
      error
    );
  }
}

/**
 * Delete menu builder layout
 */
export async function deleteMenuBuilderLayout(
  branchId: string
): Promise<void> {
  if (!branchId) {
    throw new MenuBuilderServiceError('Branch ID is required', 'INVALID_INPUT');
  }

  const { error } = await supabase
    .from('menu_builder_layouts')
    .delete()
    .eq('branch_id', branchId);

  if (error) {
    throw new MenuBuilderServiceError(
      error.message, 
      error.code, 
      error
    );
  }
}

/**
 * Check if a branch has a published layout
 */
export async function hasPublishedLayout(
  branchId: string
): Promise<boolean> {
  if (!branchId) {
    return false;
  }

  const { data, error } = await supabase
    .from('menu_builder_layouts')
    .select('is_published')
    .eq('branch_id', branchId)
    .eq('is_published', true)
    .maybeSingle();

  if (error) {
    console.error('Error checking published layout:', error);
    return false;
  }

  return !!data;
}

/**
 * Create a new layout with default content
 */
export async function createDefaultLayout(
  branchId: string
): Promise<MenuBuilderLayoutRecord> {
  const defaultLayout = createEmptyLayout();
  return saveMenuBuilderLayout(branchId, defaultLayout);
}

/**
 * Duplicate layout from one branch to another
 */
export async function duplicateLayout(
  sourceBranchId: string,
  targetBranchId: string
): Promise<MenuBuilderLayoutRecord> {
  // Fetch source layout
  const sourceLayout = await fetchMenuBuilderLayout(sourceBranchId);
  
  if (!sourceLayout) {
    throw new MenuBuilderServiceError(
      'Source layout not found',
      'NOT_FOUND'
    );
  }

  // Save to target branch (unpublished)
  return saveMenuBuilderLayout(targetBranchId, sourceLayout.layout_data);
}

/**
 * Export layout as JSON (for backup/transfer)
 */
export function exportLayoutAsJson(layout: MenuBuilderLayout): string {
  return JSON.stringify(layout, null, 2);
}

/**
 * Import layout from JSON
 */
export function importLayoutFromJson(json: string): MenuBuilderLayout {
  try {
    const parsed = JSON.parse(json);
    
    // Basic validation
    if (!parsed.version || !Array.isArray(parsed.blocks)) {
      throw new Error('Invalid layout structure');
    }
    
    return parsed as MenuBuilderLayout;
  } catch (error) {
    throw new MenuBuilderServiceError(
      'Failed to parse layout JSON',
      'PARSE_ERROR',
      error
    );
  }
}
